// src/components/quiz/QuizFlow.tsx
import { useCallback, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { posthog } from '../../lib/posthog'
import { QUESTIONS, SECTION_INTROS } from '../../lib/questions'
import type { Answer } from '../../lib/questions'
import { useQuizState } from '../../hooks/useQuizState'
import { SectionIntro } from './SectionIntro'
import { QuestionCard } from './QuestionCard'
import { ProgressRing } from './ProgressRing'
import { LoadingScreen } from './LoadingScreen'
import { ResultPage } from '../results/ResultPage'
import { scoreSubmission } from '../../lib/scoring'
import { classifyTier, getTierCopy } from '../../lib/tiers'
import { detectRedFlags } from '../../lib/redflags'
import { generateInsights } from '../../lib/insights'

interface QuizFlowProps {
  email: string
  firstName: string
}



// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function QuizFlow({ email: _email, firstName }: QuizFlowProps) {
  const { state, dispatch } = useQuizState()

  // Initialize organic sessions that came from EmailCapture
  useEffect(() => {
    if (state.phase === 'email-capture' && _email) {
      dispatch({ type: 'SET_EMAIL', email: _email, firstName })
      dispatch({ type: 'START_QUIZ' })
    }
  }, [state.phase, _email, firstName, dispatch])

  // Get current question definition
  const currentQ = QUESTIONS.find(
    q => q.section === state.currentSection && q.sectionIndex === state.currentQuestionIndex
  )

  const handleAnswer = useCallback((answerKey: string, answerLabel: string) => {
    if (!currentQ) return
    dispatch({ type: 'ANSWER', questionId: currentQ.id, answerKey, answerLabel })
    setTimeout(() => dispatch({ type: 'NEXT_QUESTION' }), 100)
  }, [currentQ, dispatch])

  const handleMultiToggle = useCallback((answerKey: string, answerLabel: string) => {
    if (!currentQ) return
    dispatch({ type: 'MULTI_SELECT_TOGGLE', questionId: currentQ.id, answerKey, answerLabel })
  }, [currentQ, dispatch])

  const handleMultiConfirm = useCallback(() => {
    if (!currentQ) return
    dispatch({ type: 'MULTI_SELECT_CONFIRM', questionId: currentQ.id })
    setTimeout(() => dispatch({ type: 'NEXT_QUESTION' }), 100)
  }, [currentQ, dispatch])

  const handleBack = useCallback(() => {
    dispatch({ type: 'PREVIOUS_QUESTION' })
  }, [dispatch])

  const handleSkip = useCallback(() => {
    dispatch({ type: 'NEXT_QUESTION' })
  }, [dispatch])

  const handleLoadingComplete = useCallback(() => {
    // Fire and forget submission to Cloudflare Worker
    const answersList = Array.from(state.answers.values())
    fetch('/api/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: _email, firstName, answers: answersList })
    }).catch(console.error)
    
    posthog.capture('quiz_completed', {
      variant: posthog.getFeatureFlag('femm-what-is-copy') ?? 'control',
    })
    dispatch({ type: 'RESULT_READY', resultId: 'local' })
  }, [state.answers, _email, firstName, dispatch])

  // ─── Hydration phase: recovering session ─────────────────
  if (state.phase === 'hydrating') {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--color-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--color-muted)', fontFamily: 'var(--font-sans)', fontSize: '0.9rem', letterSpacing: '0.05em' }}>
          Recovering your session...
        </p>
      </div>
    )
  }

  // ─── Submitting phase: show loading screen ────────────
  if (state.phase === 'submitting') {
    return <LoadingScreen onComplete={handleLoadingComplete} />
  }

  // ─── Results phase ────────────────────────────────────
  if (state.phase === 'results') {
    const answers: Answer[] = Array.from(state.answers.values())
    const scores = scoreSubmission(answers)
    const tier = classifyTier(scores.totalScore)
    const redFlags = detectRedFlags(answers)
    const insights = generateInsights(answers, scores)
    const contraceptionStatus = state.answers.get('Q22')?.answerKey
    const tierCopy = getTierCopy(tier, firstName, contraceptionStatus)
    const prevDiagnoses = (state.answers.get('Q20')?.answerKey || '').split(',')

    return (
      <ResultPage
        tier={tier}
        tierCopy={tierCopy}
        scores={scores}
        redFlags={redFlags}
        insights={insights}
        firstName={firstName}
        contraceptionStatus={contraceptionStatus}
        previousDiagnoses={prevDiagnoses}
      />
    )
  }

  // ─── Quiz phase ───────────────────────────────────────
  const sectionIntro = SECTION_INTROS[state.currentSection]
  const isWarmSection = state.currentSection === 'C'

  return (
    <div style={{ minHeight: '100dvh', position: 'relative' }}>
      <AnimatePresence mode="wait">
        {state.showSectionIntro ? (
          <motion.div
            key={`intro-${state.currentSection}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <SectionIntro
              section={state.currentSection}
              title={sectionIntro.title}
              body={sectionIntro.body}
              onContinue={() => dispatch({ type: 'DISMISS_SECTION_INTRO' })}
              isWarmSection={isWarmSection}
            />
          </motion.div>
        ) : currentQ ? (
          <motion.div
            key={`${currentQ.id}`}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}
          >
            <QuestionCard
              question={currentQ}
              section={state.currentSection}
              sectionTitle={sectionIntro.title}
              selectedAnswer={state.answers.get(currentQ.id)}
              existingMultiSelect={state.multiSelectBuffer.get(currentQ.id)}
              onAnswer={handleAnswer}
              onMultiToggle={handleMultiToggle}
              onMultiConfirm={handleMultiConfirm}
              onSkip={handleSkip}
              onBack={handleBack}
              canGoBack={state.currentSection !== 'A' || state.currentQuestionIndex > 0}
              isWarmSection={isWarmSection}
            />

            {/* Progress ring */}
            <div style={{
              position: 'fixed',
              top: 'clamp(1rem, env(safe-area-inset-top), 2rem)',
              right: '1.25rem',
              zIndex: 50,
              pointerEvents: 'none'
            }}>
              <ProgressRing
                section={state.currentSection}
                questionIndex={state.currentQuestionIndex}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
